import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import dayjs, { Dayjs } from 'dayjs';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { BlockItem } from '../admin/models/admin.model';
import { SxcService } from '../admin/sxc/sxc.service';
import { Cart, CartMode, GiftCardValidationModel, Order, OrderLog, PlaceOrder, PlaceOrderResult, SaveReservationResult } from '../cart/cart.model';
import { TermsAndConditions } from '../checkout/terms-and-conditions/terms-and-conditions';
import { FieldConfig, FieldConfigRaw, normalizeFieldsConfigs } from '../generic-fields/generic-fields';
import { GiftCardDetails } from '../gift-card/gift-card.models';
import { InfoscreenModel } from '../infoscreen/infoscreen.model';
import { ItemWithMetadata } from '../models/item';
import { MyOrderSummary } from '../models/my-order-summary';
import { AppointmentAvailability, EndDateOption, StartInfo, TimeSlotDayInfo } from '../object-choice/time-slots-picker/models';
import { UserState } from '../root-store/user-store/state';
import { Appointment, AppointmentsResult, WishValidationResult } from '../scheduler/scheduler.interfaces';
import { IdentityResult, UserResult } from '../user/user.interfaces';
import { getIconName } from '../utilities/items';
import { TreeAndItems } from './../root-store/object-tree-store/object-tree.models';
import { CatalogResponse } from './catalog.model';
import { ItemService } from './item.service';
import { LocaleService } from './locale/locale.service';
import { LabelsForCatalogResponse } from './sxc.models';
import { GUID } from './types';

@Injectable({
  providedIn: 'root'
})
export class BackendService {
  private readonly baseUrl = '/api'

  constructor(
    private http: HttpClient,
    private localeService: LocaleService,
  ) { }

  getCatalog(name: string): Observable<CatalogResponse> {
    return this.http.get<CatalogResponse>(`${this.baseUrl}/Catalog/${name}`)
  }

  getCatalogByOrderId(orderId: string): Observable<CatalogResponse> {
    return this.http.get<CatalogResponse>(`${this.baseUrl}/Catalog/Order/${orderId}`)
  }

  getCustomLabels(catalogId: number, parentCatalogId?: number) {
    let params: { [param: string]: string | number } = {
      CatalogId: catalogId,
      appId: SxcService.defaultAppId,
    }

    if (parentCatalogId)
      params['parentCatalogId'] = parentCatalogId

    return this.http.get<LabelsForCatalogResponse>(`${this.baseUrl}/sxc/app/auto/query/GetLabelsForCatalog`, { params })
      .pipe(map(res => ({
        ObjectChoice: res.ObjectChoice[0] ?? {},
        Checkout: res.Checkout[0] ?? {},
        Payment: res.Payment[0] ?? {},
      })))
  }

  getItemTree(): Observable<TreeAndItems> {
    return this.http.get<TreeAndItems>(`${this.baseUrl}/ItemTree`)
      .pipe(
        tap(treeAndItems => ItemService.prepareItemTreeData(treeAndItems))
      )
  }

  getRooms(): Observable<ItemWithMetadata[]> {
    return this.http.get<ItemWithMetadata[]>(`${this.baseUrl}/Items/Rooms`)
  }

  getItems(includeNotBookable = true): Observable<ItemWithMetadata[]> {
    // Add lang to ensure crawlers use correct language
    const params: { [param: string]: string | string[] } = {
      lang: this.localeService.currentLang()
    }

    if (!includeNotBookable)
      params['includeNotBookable'] = 'false'

    return this.http.get<ItemWithMetadata[]>(`${this.baseUrl}/Items`, { params })
      .pipe(map(allItems => allItems.map(i => ({ ...i, materialIcon: getIconName(i.itemType) }))))
  }

  getRecommendedItems(itemId: number): Observable<ItemWithMetadata[]> {
    return this.http.get<ItemWithMetadata[]>(`${this.baseUrl}/Items(${itemId})/RecommendedItems`)
  }

  getItemInfo(id: number): Observable<ItemWithMetadata> {
    return this.http.get<ItemWithMetadata>(`${this.baseUrl}/items(${id})`, {
      params: {
        // Add lang to ensure crawlers use correct language
        lang: this.localeService.currentLang()
      }
    })
  }

  getNextBookingInfo(): Observable<{ firstAvailableTime: Date }> {
    return this.http.get<{ firstAvailableTime: string }>(`${this.baseUrl}/Appointments/NextBooking`)
      .pipe(
        map(res => ({
          firstAvailableTime: new Date(res.firstAvailableTime)
        }))
      )
  }

  // #region TimeSlots
  getEndDates(availabilityPlanId: number, startDate: dayjs.Dayjs): Observable<EndDateOption[]> {
    return this.http.get<EndDateOption[]>(
      `${this.baseUrl}/TimeSlots/EndDates?availabilityPlanId=${availabilityPlanId}&startDate=${startDate.format('YYYY-MM-DD HH:mm')}`).pipe(
        map(endDates => endDates.map(res => ({ ...res, dayjs: dayjs(res.date) })))
      )
  }

  getStartInfo(planId: number | undefined, start: dayjs.Dayjs, entireDay = false, itemIds?: number[]): Observable<StartInfo> {
    const params: { [param: string]: string | boolean | number } = {
      start: start.format('YYYY-MM-DD')
    }

    if (planId)
      params['planId'] = planId
    if (entireDay)
      params['entireDay'] = true
    if (itemIds)
      params['itemIds'] = itemIds.join(',')


    return this.http.get<StartInfo>(`${this.baseUrl}/TimeSlots/StartInfo`, { params })
      .pipe(map(startInfo => ({
        ...startInfo,
        starts: startInfo.starts.map(s => ({
          ...s,
          start: dayjs(s.start),
          end: dayjs(s.end),
        }))
      })))
  }

  getNextAvailableStartDate(availabilityPlanId: number): Observable<Date> {
    return this.http.get<string>(`${this.baseUrl}/TimeSlots/NextAvailableStartDate?availabilityPlanId=${availabilityPlanId}`, { responseType: 'text' as 'json' })
      .pipe(map(dateString => dayjs(dateString).toDate()))
  }

  getAvailableStarts(planId: number, itemIds: number[]): Observable<AppointmentAvailability[]> {
    return this.http.get<AppointmentAvailability[]>(`${this.baseUrl}/TimeSlots/Starts`, { params: { planId, itemIds: itemIds.join(',') } })
      .pipe(map(availabilities => availabilities.map(a => ({ ...a, start: dayjs(a.start), end: dayjs(a.end) }))))
  }

  getTimeSlotsMonthInfo(month: number, year: number, itemIds: number[]): Observable<TimeSlotDayInfo[]> {
    return this.http.get<TimeSlotDayInfo[]>(`${this.baseUrl}/TimeSlots/MonthInfo`, { params: { month, year, itemIds: itemIds.join(',') } })
      .pipe(map(dates => dates.map(d => ({ ...d, date: dayjs(d.date) }))))
  }

  getTimeSlotsNextAvailability(itemIds: number[]): Observable<AppointmentAvailability | null> {
    return this.http.get<AppointmentAvailability | null>(`${this.baseUrl}/TimeSlots/NextAvailability`, { params: { itemIds: itemIds.join(',') } })
      .pipe(map(a => {
        if (!a)
          return a

        a.start = dayjs(a.start)
        a.end = dayjs(a.end)

        return a
      }))
  }

  // #endregion

  getAppointments(itemIds: number[], viewStart: Date, viewEnd: Date, wishStart?: Date | dayjs.Dayjs | null, wishEnd?: Date | dayjs.Dayjs | null,
    editingReservationId?: number, wishRecurrenceRule?: string | null, quantity?: number | null, consolidate?: boolean,
    availabilityPlanId?: number | null, preparationMinutes?: number, followUpMinutes?: number):
    Observable<AppointmentsResult> {
    const params = {
      itemIds: itemIds.join(','),
      wishStart: wishStart ? wishStart.toISOString() : '',
      wishEnd: wishEnd ? wishEnd.toISOString() : '',
      viewStart: viewStart.toISOString(),
      viewEnd: viewEnd.toISOString()
    } as { [param: string]: string | number | boolean }

    if (editingReservationId)
      params['editingReservationId'] = editingReservationId

    if (wishRecurrenceRule)
      params['wishRecurrenceRule'] = wishRecurrenceRule

    if (quantity && quantity > 1)
      params['quantity'] = quantity

    if (preparationMinutes !== undefined)
      params['preparationMinutes'] = preparationMinutes

    if (followUpMinutes !== undefined)
      params['followUpMinutes'] = followUpMinutes

    if (consolidate === false)
      params['consolidate'] = consolidate

    if (availabilityPlanId && availabilityPlanId > 0)
      params['availabilityPlanId'] = availabilityPlanId

    const errorResult = { appointments: [] as Appointment[], wishErrors: { invalidTime: true }, availabilities: undefined }

    // ensure viewStart and viewEnd are not before the minimum date of SQL-Server (1753-01-01)
    if (dayjs(viewStart).isBefore('1753-01-01') || dayjs(viewEnd).isBefore('1753-01-01'))
      return of(errorResult)

    return this.http.get<AppointmentsResult>(`${this.baseUrl}/Appointments`, { params }).pipe(
      catchError(() => of(errorResult)),
      map(({ appointments, wishErrors, availabilities }) => ({
        appointments: appointments.map(a => ({
          ...a,
          start: dayjs(a.start).toDate(),
          end: dayjs(a.end).toDate(),
          title: (a as any).subject,
          tooltip: a.tooltip?.replace(/\r\n/g, '<br/>'),
          isWishTime: typeof a.id === 'string' && a.id.startsWith('WishTime')
        })),
        wishErrors,
        availabilities: availabilities?.map(a => ({ ...a, start: dayjs(a.start), end: dayjs(a.end) }))
      })))
  }

  getAppointment(reservationId: number) {
    return this.http.get<{ userIdentifier: GUID, allowEdit: boolean } | null>(`${this.baseUrl}/Appointments(${reservationId})`)
  }

  getItemsAvailability(start: dayjs.Dayjs, end: dayjs.Dayjs, itemIds: number[]): Observable<WishValidationResult> {
    const params = {
      wishStart: start.toISOString(),
      wishEnd: end.toISOString(),
      itemIds: itemIds.join(',')
    }

    return this.http.get<WishValidationResult>(`${this.baseUrl}/Appointments/Availability`, { params })
  }

  getAvailableQuantity(start: dayjs.Dayjs, end: dayjs.Dayjs, itemIds: number[], editingReservationId?: number) {
    const params = {
      start: start.toISOString(),
      end: end.toISOString(),
      itemIds: itemIds.join(',')
    } as { [k: string]: string }

    if (editingReservationId)
      params['editingReservationId'] = editingReservationId.toString()

    return this.http.get<number | null>(`${this.baseUrl}/Appointments/AvailableQuantity`, { params })
  }

  getMyOrders(): Observable<MyOrderSummary[]> {
    return this.http.get<MyOrderSummary[]>(`${this.baseUrl}/Orders/MyOrders`)
  }

  getOrder(cartMode: CartMode, orderId?: GUID): Observable<Order> {
    const params: { [param: string]: string } = {
      cartMode
    }

    if (orderId)
      params['OrderId'] = orderId

    return this.http.get<Order>(`${this.baseUrl}/Cart/Order`, { params })
  }

  // #region User
  getUser(): Observable<UserState> {
    return this.http.get<UserState>(`${this.baseUrl}/User`)
  }

  loginUser(body: any): Observable<UserResult> {
    return this.http.post<UserResult>(`${this.baseUrl}/User/Login`, body)
      .pipe(tap(res => this.requestVerificationToken = res.requestVerificationToken))
  }

  logoutUser(): Observable<UserResult> {
    return this.http.post<UserResult>(`${this.baseUrl}/User/Logout`, null)
      .pipe(tap(res => this.requestVerificationToken = res.requestVerificationToken))
  }

  forgotPassword(email: string): Observable<IdentityResult> {
    return this.http.post<IdentityResult>(`${this.baseUrl}/User/ForgotPassword`, { email })
  }

  resetPassword(model: { email: string, code: string, newPassword: string }): Observable<UserResult> {
    return this.http.post<UserResult>(`${this.baseUrl}/User/ResetPassword`, model).pipe(tap(res => {
      this.requestVerificationToken = res.requestVerificationToken
    }))
  }

  changePassword(model: { email: string, currentPassword: string, newPassword: string }): Observable<IdentityResult> {
    return this.http.post<IdentityResult>(`${this.baseUrl}/User/ChangePassword`, model)
  }

  registerUser(model: any): Observable<UserResult> {
    return this.http.post<UserResult>(`${this.baseUrl}/User/Register`, model)
      .pipe(tap(res => this.requestVerificationToken = res.requestVerificationToken))
  }

  updateMyProfile(model: any): Observable<UserResult> {
    return this.http.post<UserResult>(`${this.baseUrl}/User/UpdateMyProfile`, model)
      .pipe(tap(res => this.requestVerificationToken = res.requestVerificationToken))
  }
  // #endregion

  // #region Cart
  // All calls to ${this.baseUrl}/Cart may optionally have an ?OrderId QueryString-Parameter, which might be added by an interceptor
  updateCartCustomerGroup(customerGroupId: number): Observable<any> {
    return this.http.put(`${this.baseUrl}/Cart/CustomerGroup?customerGroupId=${customerGroupId}`, {})
  }

  getCart(cartMode: CartMode, orderId?: GUID): Observable<Cart> {
    const params: { [param: string]: string } = {}
    if (cartMode !== 'Default')
      params['cartMode'] = cartMode
    if (orderId)
      params['OrderId'] = orderId

    return this.http.get<Cart>(`${this.baseUrl}/Cart`, { params })
  }

  keepAlive(): Observable<any> {
    return this.http.post(`${this.baseUrl}/Cart/KeepAlive`, undefined)
  }

  deleteReservation(reservationId: number): Observable<SaveReservationResult> {
    return this.http.delete<SaveReservationResult>(`${this.baseUrl}/Cart(${reservationId})`)
  }

  addToCart(orderFields: any, params: { [param: string]: string | boolean | number }): Observable<SaveReservationResult> {
    return this.http.post<SaveReservationResult>(`${this.baseUrl}/Cart/Add`, { orderFields }, { params })
  }

  addAdditionalEntry(
    itemId: number | null, start: Date | null, end: Date | null, quantity: number | null, manualPrice: boolean, price: number | null,
    comment: string | null, externalComment: string | null): Observable<SaveReservationResult> {
    return this.addUpdateAdditionalEntry(null, itemId, start, end, quantity, manualPrice, price, comment, externalComment)
  }

  addFromOtherOrder(reservationIds: number[], move: boolean) {
    return this.http.post<SaveReservationResult>(`${this.baseUrl}/Cart/AddFromOtherOrder`, undefined, {
      params: { reservationIds: reservationIds.join(','), move }
    })
  }

  updateCustomPosition(
    reservationId: number, start: Date | null, end: Date | null, quantity: number | null, manualPrice: boolean, price: number | null,
    comment: string | null, externalComment: string | null): Observable<SaveReservationResult> {
    return this.addUpdateAdditionalEntry(reservationId, null, start, end, quantity, manualPrice, price, comment, externalComment)
  }

  private addUpdateAdditionalEntry(
    reservationId: number | null, itemId: number | null, start: Date | null, end: Date | null, quantity: number | null, manualPrice: boolean,
    price: number | null, comment: string | null, externalComment: string | null): Observable<SaveReservationResult> {
    const params: { [param: string]: string } = {
      manualPrice: manualPrice.toString()
    }

    if (itemId)
      params['itemId'] = itemId.toString()
    if (start)
      params['start'] = start.toISOString()
    if (end)
      params['end'] = end.toISOString()
    if (price)
      params['price'] = price.toString()
    if (quantity)
      params['quantity'] = quantity.toString()
    if (comment)
      params['comment'] = comment
    if (externalComment)
      params['externalComment'] = externalComment

    return !reservationId ?
      this.http.post<SaveReservationResult>(`${this.baseUrl}/Cart/Additional`, {}, { params }) :
      this.http.put<SaveReservationResult>(`${this.baseUrl}/Cart/Additional(${reservationId})`, {}, { params })
  }

  collapseRecurrence(reservationId: number): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/Cart(${reservationId})/Collapse`, {})
  }

  expandRecurrence(reservationId: number): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/Cart(${reservationId})/Expand`, {})
  }

  acceptChangeRequest(reservationId: number): Observable<boolean> {
    return this.http.patch<boolean>(`${this.baseUrl}/Cart(${reservationId})/AcceptChangeRequest`, {})
  }

  rejectChangeRequest(reservationId: number): Observable<boolean> {
    return this.http.patch<boolean>(`${this.baseUrl}/Cart(${reservationId})/RejectChangeRequest`, {})
  }

  getOrderLog() {
    return this.http.get<OrderLog[]>(`${this.baseUrl}/Cart/OrderLog`)
  }

  //#region BlockAppointments

  addBlock(orderFields: { [key: number]: { [key: string]: string } }, params: { [param: string]: string | boolean | number }): Observable<SaveReservationResult> {
    return this.http.post<SaveReservationResult>(`${this.baseUrl}/Cart/AddBlock`, { orderFields }, { params })
  }

  updateBlockAppointment(id: number, start: Dayjs, end: Dayjs, comment?: string | null, externalComment?: string | null,
    availabilityPlanId?: number | null, quantity?: number | null, isPrintable?: boolean | null,
    printCol1?: string | null, printCol2?: string | null, printCol3?: string | null, doubleReservationAllowed?: boolean, mailRecipientOnOverlappingWithBlockInfo?: string | null): Observable<BlockItem> {

    const params: { [param: string]: string | boolean } = {
      start: start.toISOString(),
      end: end!.toISOString()
    }

    if (comment)
      params['comment'] = comment

    if (externalComment)
      params['externalComment'] = externalComment

    if (availabilityPlanId)
      params['availabilityPlanId'] = availabilityPlanId.toString()

    if (quantity)
      params['quantity'] = quantity.toString()

    if (isPrintable)
      params['isPrintable'] = isPrintable

    if (printCol1)
      params['printCol1'] = printCol1

    if (printCol2)
      params['printCol2'] = printCol2

    if (printCol3)
      params['printCol3'] = printCol3

    if (doubleReservationAllowed !== undefined)
      params['doubleReservationAllowed'] = doubleReservationAllowed

    if (mailRecipientOnOverlappingWithBlockInfo)
      params['mailRecipientOnOverlappingWithBlockInfo'] = mailRecipientOnOverlappingWithBlockInfo

    return this.http.put<BlockItem>(`${this.baseUrl}/Blocks(${id})`, {}, { params })
  }

  deleteBlockAppointment(id: number): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/Blocks(${id})`)
  }

  copyBlockAppointment(id: number): Observable<BlockItem> {
    return this.http.post<BlockItem>(`${this.baseUrl}/Blocks(${id})/Copy`, {})
  }

  getBlockAppointment(id: number): Observable<BlockItem> {
    return this.http.get<BlockItem>(`${this.baseUrl}/Blocks(${id})`)
  }

  // #endregion

  orderCartItemsByStartDate(): Observable<any> {
    return this.http.post(`${this.baseUrl}/Cart/OrderItemsByStartDate`, {})
  }

  orderCartItems(reservationIds: number[]): Observable<any> {
    return this.http.post(`${this.baseUrl}/Cart/OrderItems?reservationIds=${reservationIds.join(',')}`, {})
  }

  updateCartObjectField(fieldId: number, newValue: string, reservationId: number): Observable<any> {
    return this.http.put(`${this.baseUrl}/Cart/ObjectField`, {},
      { params: { fieldId: fieldId, newValue, reservationId: reservationId } })
  }

  updateCartItem(reservationId: number, start: Dayjs, end: Dayjs, comment?: string | null, externalComment?: string | null,
    availabilityPlanId?: number | null, quantity?: number | null, isPrintable?: boolean | null,
    printCol1?: string | null, printCol2?: string | null, printCol3?: string | null, itemFieldValues?: { [key: string]: string },
    manualPrice?: boolean, price?: number | null, preparationMinutes?: number, followUpMinutes?: number
  ): Observable<SaveReservationResult> {

    const params: { [param: string]: string | boolean | number } = {
      start: start.toISOString(),
      end: end!.toISOString()
    } as { [param: string]: string | boolean | number }

    if (preparationMinutes)
      params['preparationMinutes'] = preparationMinutes

    if (followUpMinutes)
      params['followUpMinutes'] = followUpMinutes

    if (comment)
      params['comment'] = comment

    if (externalComment)
      params['externalComment'] = externalComment

    if (availabilityPlanId)
      params['availabilityPlanId'] = availabilityPlanId

    if (quantity)
      params['quantity'] = quantity

    if (isPrintable)
      params['isPrintable'] = isPrintable

    if (printCol1)
      params['printCol1'] = printCol1

    if (printCol2)
      params['printCol2'] = printCol2

    if (printCol3)
      params['printCol3'] = printCol3

    if (manualPrice === true) {
      params['manualPrice'] = manualPrice

      if (price !== undefined && price !== null)
        params['price'] = price
    }

    return this.http.put<SaveReservationResult>(`${this.baseUrl}/Cart(${reservationId})`, { itemFieldValues }, { params })
  }

  placeOrder(order: PlaceOrder): Observable<PlaceOrderResult> {
    return this.http.post<PlaceOrderResult>(`${this.baseUrl}/Cart/PlaceOrder`, order)
  }

  getCheckoutFields(): Observable<FieldConfig[]> {
    return this.http.get<FieldConfigRaw[]>(`${this.baseUrl}/Cart/CheckoutFields`)
      .pipe(map(raw => normalizeFieldsConfigs(raw)))
  }

  getICalUrl(orderId: string, catName: string, reservationId: number): string {
    return `${this.baseUrl}/Cart/IcalExport?OrderId=${orderId}&catName=${catName}&reservationId=${reservationId}`
  }

  // Admin-Cart
  acceptReservation(reservationId: number): Observable<boolean> {
    return this.http.patch<boolean>(`${this.baseUrl}/Cart(${reservationId})/Accept`, {})
  }

  rejectReservation(reservationId: number): Observable<boolean> {
    return this.http.patch<boolean>(`${this.baseUrl}/Cart(${reservationId})/Reject`, {})
  }

  acceptAllReservations(): Observable<boolean> {
    return this.http.patch<boolean>(`${this.baseUrl}/Cart/AcceptAll`, {})
  }

  rejectAllReservations(): Observable<boolean> {
    return this.http.patch<boolean>(`${this.baseUrl}/Cart/RejectAll`, {})
  }

  // #endregion

  //#region GiftCards

  addGiftCard(itemId: number, price: number, senderName: string, recipientName: string, message: string) {
    return this.http.post<void>(`${this.baseUrl}/Cart/AddGiftCard`, undefined, { params: { itemId, price, senderName, recipientName, message } })
  }

  updateGiftCard(reservationId: number, price: number, senderName: string, recipientName: string, message: string) {
    return this.http.put<void>(`${this.baseUrl}/Cart/UpdateGiftCard`, undefined, { params: { reservationId, price, senderName, recipientName, message } })
  }

  getGiftCard(couponCode: string) {
    return this.http.get<GiftCardDetails>(`${this.baseUrl}/GiftCards(${couponCode})`)
  }

  validateGiftCard(couponCode: string) {
    return this.http.get<GiftCardValidationModel>(`${this.baseUrl}/Cart/ValidateGiftCard`, { params: { couponCode } })
  }

  applyGiftCard(couponCode: string) {
    return this.http.post<void>(`${this.baseUrl}/Cart/ApplyGiftCard`, undefined, { params: { couponCode } })
  }

  removeGiftCard(couponCode: string) {
    return this.http.delete<void>(`${this.baseUrl}/Cart/RemoveGiftCard`, { params: { couponCode } })
  }

  getGiftCardPreviewUrl(amount: number, catalogId: number, senderName?: string, recipientName?: string, message?: string): string {
    let params = new HttpParams()

    if (senderName)
      params = params.set('senderName', senderName)

    if (recipientName)
      params = params.set('recipientName', recipientName)

    if (message)
      params = params.set('message', message)

    return `${this.baseUrl}/GiftCards/Preview?amount=${amount}&catalogId=${catalogId}&${params}`
  }

  getGiftCardUrl(couponCode: string): string {
    return `${this.baseUrl}/GiftCards/${couponCode}`
  }

  //#endregion

  //#region XSRF
  get requestVerificationToken(): string | null {
    return document.getElementById('__RequestVerificationToken')?.getAttribute('value') ?? null
  }

  set requestVerificationToken(value: string | null) {
    if (!value) return

    document.getElementById('__RequestVerificationToken')?.setAttribute('value', value)
  }
  //#endregion

  getTermsAndConditions(includeParentCatalog = true): Observable<TermsAndConditions[]> {
    let params = new HttpParams()

    if (!includeParentCatalog)
      params = params.set('includeParentCatalog', false)

    return this.http.get<TermsAndConditions[]>(`${this.baseUrl}/Catalog/TermsAndConditions`, { params })
  }

  getInfoscreen(key: string) {
    return this.http.get<InfoscreenModel>(`${this.baseUrl}/Infoscreen/${key}`)
  }

  getInfoscreenPreview(futureEvents: string, pastEvents: string, showPurpose: boolean, showAllBookingDetails: boolean, allItems: boolean, itemIds?: number[]) {
    return this.http.get<InfoscreenModel>(`${this.baseUrl}/Infoscreen/Preview`, {
      params: {
        futureEvents,
        pastEvents,
        showPurpose,
        showAllBookingDetails,
        allItems,
        itemIds: itemIds?.join(',') ?? ''
      }
    })
  }
}
